import React from "react";
import { MapStyleId, MAP_STYLE_IDS } from "../../types";
import { SettingRow } from "./SettingRow";
import { ColorPicker } from "./ColorPicker";
import { MapStyleButton } from "../common/MapStyleButton";
import debugx from "debugx";

const debug = debugx("bikedash:settings");

interface MapTabProps {
  userPreferences: any;
  mapPreferences: any;
  updateUserPreferences: (updates: any) => void;
  updateMapPreferences: (updates: any) => void;
  toggleStarredMap: (styleId: MapStyleId) => void;
}

// Helper function to calculate outline color for preview
function getOutlineColorForPreview(
  lineColor: string,
  outlineColor: string | undefined,
  showOutline: boolean,
): string {
  if (!showOutline) return "none";
  if (!outlineColor || outlineColor === "none") return "none";
  if (outlineColor === "auto") {
    // Calculate based on brightness
    let r: number, g: number, b: number;
    if (lineColor.startsWith("#") && lineColor.length === 7) {
      r = parseInt(lineColor.slice(1, 3), 16);
      g = parseInt(lineColor.slice(3, 5), 16);
      b = parseInt(lineColor.slice(5, 7), 16);
    } else if (lineColor.startsWith("rgb")) {
      const match = lineColor.match(/rgba?\(([^)]+)\)/);
      if (match) {
        const parts = match[1].split(",").map((p) => parseFloat(p.trim()));
        r = parts[0] || 0;
        g = parts[1] || 0;
        b = parts[2] || 0;
      } else {
        return "0 0 0 1px #ffffff";
      }
    } else {
      return "0 0 0 1px #ffffff";
    }
    const luminance = 0.299 * r! + 0.587 * g! + 0.114 * b!;
    return luminance > 180 ? "0 0 0 1px #000000" : "0 0 0 1px #ffffff";
  }
  return `0 0 0 1px ${outlineColor}`;
}

export function MapTab({
  userPreferences,
  mapPreferences,
  updateUserPreferences,
  updateMapPreferences,
  toggleStarredMap,
}: MapTabProps) {
  // Map style groups
  const freeStyles: MapStyleId[] = [
    "osm:standard",
    "osm:humanitarian",
    "osm:topographic",
    "cartodb:light",
    "cartodb:dark",
    "cartodb:voyager",
    "esri:gray",
    "esri:satellite",
  ];

  const thunderforestStyles: MapStyleId[] = [
    "thunderforest:cycle",
    "thunderforest:landscape",
    "thunderforest:outdoors",
    "thunderforest:transport",
    "thunderforest:transport-dark",
    "thunderforest:spinal-map",
    "thunderforest:pioneer",
    "thunderforest:mobile-atlas",
    "thunderforest:neighbourhood",
  ];

  // Check for API key from cached auth first, then manual override
  const cachedAuth = localStorage.getItem("bikedash_cached_auth");
  let hasApiKey = false;
  if (cachedAuth) {
    try {
      const cached = JSON.parse(cachedAuth);
      hasApiKey = !!cached.thunderforestApiKey;
    } catch (e) {
      // Ignore
    }
  }
  // Fallback to manual override
  if (!hasApiKey) {
    hasApiKey = !!userPreferences.apiKeys?.thunderforest;
  }

  const starredStyles = mapPreferences.starredStyleIds.filter((id: string) =>
    MAP_STYLE_IDS.includes(id as MapStyleId),
  ) as MapStyleId[];

  return (
    <div className="settings-section">
      {/* Map Options */}
      <div className="settings-group-header">Map Options</div>

      <div className="setting-row">
        <div className="setting-info">
          <div className="setting-label">Satellite overlay</div>
          <p className="setting-description">
            Blend satellite imagery with map (0% = map only, 10-80% = blended)
          </p>
        </div>
        <div
          className="slider-controls"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-md)",
            flex: 1,
            marginLeft: "var(--spacing-lg)",
          }}
        >
          <span style={{ fontSize: "14px" }}>🛰️</span>
          <input
            type="range"
            min="0"
            max="80"
            value={(() => {
              const opacity = mapPreferences.overlayOpacity || 0;
              if (opacity === 0) return 0;
              return Math.round(opacity * 100);
            })()}
            onChange={(e) => {
              let value = Number(e.target.value);
              let actualValue: number;
              if (value === 0) {
                actualValue = 0;
              } else {
                let snapped = Math.round(value / 10) * 10;
                if (snapped === 0) snapped = 10;
                actualValue = snapped / 100;
              }
              updateMapPreferences({
                overlayOpacity: actualValue,
              });
              debug(
                "Satellite overlay set to: %d%% (actual: %d%%)",
                value,
                Math.round(actualValue * 100),
              );
            }}
            style={{
              flex: 1,
            }}
            className="opacity-slider"
          />
          <span
            style={{
              fontSize: "13px",
              color: "var(--text-primary)",
              minWidth: "40px",
              fontWeight: 500,
              textAlign: "right",
            }}
          >
            {mapPreferences.overlayOpacity === 0
              ? "OFF"
              : `${Math.round((mapPreferences.overlayOpacity || 0) * 100)}%`}
          </span>
        </div>
      </div>

      <SettingRow
        label="Smooth GPS motion"
        description="Animate position changes smoothly"
        checked={!!mapPreferences.smoothMotion}
        onChange={(checked) => {
          debug("Smooth motion toggled to:", checked);
          updateMapPreferences({ smoothMotion: checked });
        }}
      />

      <SettingRow
        label="Use GPS Heading"
        description="Use GPS direction of travel when speed > 2mph (no motion permission needed)"
        checked={!!mapPreferences.useGpsHeading}
        onChange={(checked) => {
          debug("GPS heading toggled to:", checked);
          updateMapPreferences({ useGpsHeading: checked });
        }}
      />

      <div className="setting-group">
        <label>Max Update Rate</label>
        <p className="setting-description">
          Animation frame rate for compass and map updates
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "8px",
            height: "60px",
            marginTop: "8px",
          }}
        >
          {[1, 2, 4, 10, 30].map((fps) => (
            <button
              key={fps}
              onClick={() => updateUserPreferences({ maxUpdateRate: fps })}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 4px",
                background:
                  (userPreferences.maxUpdateRate || 30) === fps
                    ? "rgba(62, 106, 225, 0.3)"
                    : "var(--bg-subtle)",
                border:
                  (userPreferences.maxUpdateRate || 30) === fps
                    ? "2px solid var(--accent)"
                    : "1px solid var(--border)",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  color:
                    (userPreferences.maxUpdateRate || 30) === fps
                      ? "var(--text-primary)"
                      : "var(--text-secondary)",
                  fontWeight:
                    (userPreferences.maxUpdateRate || 30) === fps ? 600 : 500,
                  marginBottom: "2px",
                }}
              >
                {fps}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "var(--text-secondary)",
                }}
              >
                fps
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="setting-row">
        <div className="setting-info">
          <div className="setting-label">Track Style</div>
          <p className="setting-description">
            Width and color for activity track lines
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-md)",
          }}
        >
          <input
            type="range"
            min="1"
            max="15"
            value={mapPreferences.activityTrack?.lineWidth || 4}
            onChange={(e) => {
              const value = Number(e.target.value);
              updateMapPreferences({
                activityTrack: {
                  ...mapPreferences.activityTrack!,
                  lineWidth: value,
                },
              });
            }}
            className="track-width-slider"
            style={
              {
                "--track-color":
                  mapPreferences.activityTrack?.lineColor || "#3e6ae1",
                "--track-width": `${mapPreferences.activityTrack?.lineWidth || 4}px`,
                "--track-outline": getOutlineColorForPreview(
                  mapPreferences.activityTrack?.lineColor || "#3e6ae1",
                  mapPreferences.activityTrack?.outlineColor || "auto",
                  mapPreferences.activityTrack?.showOutline ?? true,
                ),
                width: "100px",
              } as React.CSSProperties
            }
          />
          <span
            style={{
              fontSize: "12px",
              color: "var(--text-secondary)",
              minWidth: "25px",
            }}
          >
            {mapPreferences.activityTrack?.lineWidth || 4}pt
          </span>
          <ColorPicker
            value={mapPreferences.activityTrack?.lineColor || "#3e6ae1"}
            onChange={(color) => {
              updateMapPreferences({
                activityTrack: {
                  ...mapPreferences.activityTrack!,
                  lineColor: color,
                },
              });
            }}
            showOutlineToggle={true}
            outlineEnabled={mapPreferences.activityTrack?.showOutline ?? true}
            onOutlineChange={(enabled) => {
              updateMapPreferences({
                activityTrack: {
                  ...mapPreferences.activityTrack!,
                  showOutline: enabled,
                },
              });
            }}
            outlineColor={mapPreferences.activityTrack?.outlineColor || "auto"}
            onOutlineColorChange={(color) => {
              updateMapPreferences({
                activityTrack: {
                  ...mapPreferences.activityTrack!,
                  outlineColor: color,
                  // Auto-enable outline when selecting a color
                  showOutline: true,
                },
              });
            }}
          />
        </div>
      </div>

      <div className="setting-row">
        <div className="setting-info">
          <div className="setting-label">Route Style</div>
          <p className="setting-description">
            Width and color for simulation/navigation route lines
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-md)",
          }}
        >
          <input
            type="range"
            min="1"
            max="15"
            value={mapPreferences.routeTrack?.lineWidth || 3}
            onChange={(e) => {
              const value = Number(e.target.value);
              updateMapPreferences({
                routeTrack: {
                  ...mapPreferences.routeTrack!,
                  lineWidth: value,
                },
              });
            }}
            className="track-width-slider"
            style={
              {
                "--track-color":
                  mapPreferences.routeTrack?.lineColor || "#22c55e",
                "--track-width": `${mapPreferences.routeTrack?.lineWidth || 3}px`,
                "--track-outline": getOutlineColorForPreview(
                  mapPreferences.routeTrack?.lineColor || "#22c55e",
                  mapPreferences.routeTrack?.outlineColor || "auto",
                  mapPreferences.routeTrack?.showOutline ?? true,
                ),
                width: "100px",
              } as React.CSSProperties
            }
          />
          <span
            style={{
              fontSize: "12px",
              color: "var(--text-secondary)",
              minWidth: "25px",
            }}
          >
            {mapPreferences.routeTrack?.lineWidth || 3}pt
          </span>
          <ColorPicker
            value={mapPreferences.routeTrack?.lineColor || "#22c55e"}
            onChange={(color) => {
              updateMapPreferences({
                routeTrack: {
                  ...mapPreferences.routeTrack!,
                  lineColor: color,
                },
              });
            }}
            showOutlineToggle={true}
            outlineEnabled={mapPreferences.routeTrack?.showOutline ?? true}
            onOutlineChange={(enabled) => {
              updateMapPreferences({
                routeTrack: {
                  ...mapPreferences.routeTrack!,
                  showOutline: enabled,
                },
              });
            }}
            outlineColor={mapPreferences.routeTrack?.outlineColor || "auto"}
            onOutlineColorChange={(color) => {
              updateMapPreferences({
                routeTrack: {
                  ...mapPreferences.routeTrack!,
                  outlineColor: color,
                  // Auto-enable outline when selecting a color
                  showOutline: true,
                },
              });
            }}
          />
        </div>
      </div>

      {/* Map Styles */}
      <div className="settings-group-header">Map Styles</div>

      {/* Starred Maps */}
      {starredStyles.length > 0 && (
        <>
          <div className="map-category">⭐ Starred Maps</div>
          <div className="map-grid">
            {starredStyles.map((styleId) => (
              <MapStyleButton
                key={`${styleId}-starred`}
                styleId={styleId}
                isStarred={true}
                isSelected={mapPreferences.defaultStyleId === styleId}
                isUnavailable={
                  thunderforestStyles.includes(styleId) && !hasApiKey
                }
                onToggleStar={() => toggleStarredMap(styleId)}
                onSelect={() =>
                  updateMapPreferences({ defaultStyleId: styleId })
                }
              />
            ))}
          </div>
        </>
      )}

      {/* Free Maps */}
      <div className="map-category">🆓 Open Maps</div>
      <div className="map-grid">
        {freeStyles.map((styleId) => (
          <MapStyleButton
            key={styleId}
            styleId={styleId}
            isStarred={mapPreferences.starredStyleIds.includes(styleId)}
            isSelected={mapPreferences.defaultStyleId === styleId}
            isUnavailable={false}
            onToggleStar={() => toggleStarredMap(styleId)}
            onSelect={() => updateMapPreferences({ defaultStyleId: styleId })}
          />
        ))}
      </div>

      {/* Thunderforest Maps */}
      <div className="map-category">
        🗺️ Thunderforest Maps
        {!hasApiKey && <span className="api-badge">Login for Access</span>}
      </div>
      <div className="map-grid">
        {thunderforestStyles.map((styleId) => (
          <MapStyleButton
            key={styleId}
            styleId={styleId}
            isStarred={mapPreferences.starredStyleIds.includes(styleId)}
            isSelected={mapPreferences.defaultStyleId === styleId}
            isUnavailable={!hasApiKey}
            onToggleStar={() => toggleStarredMap(styleId)}
            onSelect={() => {
              if (!hasApiKey) return;
              updateMapPreferences({ defaultStyleId: styleId });
            }}
          />
        ))}
      </div>
    </div>
  );
}
